// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useState } from 'react';
import DataTypeRenderer from './DataTypeRenderer';
import { LayeredComparisonRenderer } from './LayeredComparisonRenderer';

interface SectionRendererProps {
  sectionName: string;
  sectionData: any;
}

/**
 * Get icon based on section name
 */
const getSectionIcon = (name: string) => {
  if (name.toLowerCase().includes('summary') || name.toLowerCase().includes('executive')) {
    return '🎯';
  }
  if (name.toLowerCase().includes('recommendation')) {
    return '💡';
  }
  if (name.toLowerCase().includes('comparison')) {
    return '📊';
  }
  if (name.toLowerCase().includes('concept')) {
    return '🧠';
  }
  if (name.toLowerCase().includes('difficulty')) {
    return '📈';
  }
  if (name.toLowerCase().includes('problem')) {
    return '🔍';
  }
  if (name.toLowerCase().includes('layer')) {
    return '📋';
  }
  return '📄';
};

/**
 * Determine section styling based on name
 */
const getSectionStyle = (name: string) => {
  if (name.toLowerCase().includes('recommendation')) {
    return 'bg-white border border-gray-200';
  }
  if (name.toLowerCase().includes('comparison') || name.toLowerCase().includes('analysis')) {
    return 'bg-white border border-gray-200';
  }
  if (name.toLowerCase().includes('difficulty') || name.toLowerCase().includes('problem')) {
    return 'bg-gradient-to-br from-orange-50 to-yellow-50 border-l-4 border-orange-500';
  }
  return 'bg-white border border-gray-200';
};

/**
 * Renders individual sections of the LLM response.
 * Determines the best rendering approach based on section name and data type.
 */
const SectionRenderer: React.FC<SectionRendererProps> = ({ sectionName, sectionData }) => {
  // Determine if this section should be collapsible and start collapsed
  // But not in PDF export mode
  const shouldStartCollapsed = sectionName.toLowerCase().includes('recommendation');
  const [isCollapsed, setIsCollapsed] = useState(shouldStartCollapsed);
  
  // Check if we're in PDF export mode
  const [isPDFMode, setIsPDFMode] = React.useState(false);
  
  React.useEffect(() => {
    // Check if parent has pdf-export-mode class
    const checkPDFMode = () => {
      const pdfMode = document.querySelector('.pdf-export-mode') !== null;
      setIsPDFMode(pdfMode);
    };
    
    checkPDFMode();
    
    // Set up a mutation observer to detect when PDF mode is added
    const observer = new MutationObserver(checkPDFMode);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  
  if (sectionData === null || sectionData === undefined) {
    return null; // Don't render empty sections
  }

  // Check if this is a layered comparison section - use specialized renderer
  const layeredSections = [
    'executive_summary',
    'layer_1_document_structure',
    'layer_2_concept_coverage_analysis',
    'layer_3_problem_topics',
    'layer_3_problem_by_problem_concept_analysis'

  ];
  
  if (layeredSections.includes(sectionName)) {
    const layeredRender = (
      <LayeredComparisonRenderer sectionName={sectionName} sectionData={sectionData} />
    );
    
    // If layered renderer returns something, use it
    if (layeredRender) {
      const displayName = sectionName
        .replace(/^layer_\d+_/i, '') // Remove "layer_1_", "layer_2_", etc. prefix
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
      
      return (
        <div className="p-6 rounded-xl shadow-lg bg-white border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <span className="text-2xl mr-3">{getSectionIcon(sectionName)}</span>
            {displayName}
          </h3>
          {layeredRender}
        </div>
      );
    }
  }

  // Format section name for display
  const displayName = sectionName
    .replace(/^layer_\d+_/i, '') // Remove "layer_1_", "layer_2_", etc. prefix
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

  const getSectionStyle = (name: string) => {
    // if (name.toLowerCase().includes('summary') || name.toLowerCase().includes('executive')) {
    //   return 'bg-gradient-to-br from-blue-50 to-purple-50';
    // }
    if (name.toLowerCase().includes('recommendation')) {
      return 'bg-white border border-gray-200';
    }
    if (name.toLowerCase().includes('comparison') || name.toLowerCase().includes('analysis')) {
      return 'bg-white border border-gray-200';
    }
    if (name.toLowerCase().includes('difficulty') || name.toLowerCase().includes('problem')) {
      return 'bg-gradient-to-br from-orange-50 to-yellow-50 border-l-4 border-orange-500';
    }
    return 'bg-white border border-gray-200';
  };

  const sectionStyle = getSectionStyle(sectionName);
  const sectionIcon = getSectionIcon(sectionName);
  
  // Determine if this section should be collapsible
  const isCollapsible = sectionName.toLowerCase().includes('recommendation');

  return (
    <div className={`p-4 rounded-lg shadow-md ${sectionStyle}`}>
      <h3 
        className={`text-lg font-bold text-gray-800 mb-3 flex items-center justify-between ${isCollapsible ? 'cursor-pointer' : ''}`}
        onClick={() => isCollapsible && setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center">
          <span className="text-2xl mr-3">{sectionIcon}</span>
          {displayName}
        </div>
        {isCollapsible && (
          <button
            className="text-gray-600 hover:text-gray-800 transition-transform"
            style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
             aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
    aria-expanded={!isCollapsed}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </h3>
      
      {(!isCollapsed || isPDFMode) && (
        <div className={isPDFMode ? 'pdf-expand-all' : ''}>
          <DataTypeRenderer data={sectionData} />
        </div>
      )}
    </div>
  );
};

export default SectionRenderer;